import { Injectable } from "@angular/core";

import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { flatMap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { Roles } from "../components/auth/auth.role";
import { AuthToken } from "../components/auth/auth.token";
import { Generate2FAReturnModel } from "../model/domain/Generate2FAReturnModel";
import { MFAValidateCodeModel } from "../model/domain/MFAValidateCodeModel";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  authKey = ".authToken";
  authToken: AuthToken = null;

  constructor(private http: HttpClient) {}

  // store the URL so we can redirect after logging in
  redirectUrl: string;

  login(username: string, password: string): Observable<AuthToken> {
    return this.http.post<AuthToken>(
      environment.apiUrls.security + "/auth/token",
      {
        username,
        password,
      }
    );
  }

  validateMFACode(model: MFAValidateCodeModel): Observable<AuthToken> {
    return this.http
      .post<AuthToken>(
        `${environment.apiUrls.security}/auth/validate2Fa-code`,
        model
      )
      .pipe(
        flatMap((authTokenResult) => {
          this.authToken = authTokenResult;
          return of(authTokenResult);
        })
      );
  }

  generateQrCode(
    model: MFAValidateCodeModel
  ): Observable<Generate2FAReturnModel> {
    return this.http.post<Generate2FAReturnModel>(
      `${environment.apiUrls.security}/auth/generate-shared2Fa-code`,
      model
    );
  }

  roles() {
    return this.http
      .get<number[]>(environment.apiUrls.security + "/auth/user/roles")
      .pipe(
        flatMap((authRolesResult) => {
          this.authToken.roles = authRolesResult as number[];

          if (this.authToken && this.authToken.roles) {
            localStorage.setItem(this.authKey, JSON.stringify(this.authToken));
            return of(true);
          }

          return of(false);
        })
      );
  }

  rolesByUsername(username: string) {
    return this.http.get<number[]>(
      environment.apiUrls.security + `/auth/user/roles/${username}`
    );
  }

  passwordForgot(username: string) {
    return this.http.post(
      environment.apiUrls.security + "/auth/password-forgot",
      { username }
    );
  }

  passwordResetByAdmin(username: string) {
    return this.http.post(
      environment.apiUrls.security + "/auth/password-reset-by-admin",
      { username }
    );
  }

  resetPassword(
    username: string,
    oldPassword: string,
    newPassword: string,
    passwordResetHash: string = null
  ) {
    return this.http.post<AuthToken>(
      environment.apiUrls.security + "/auth/reset-password",
      { username, oldPassword, newPassword, passwordResetHash }
    );
  }

  logout(): void {
    this.authToken = null;
    localStorage.removeItem(this.authKey);
  }

  getToken(): string {
    const auth = this.getAuth();
    if (auth) {
      return auth.token;
    }
    return "";
  }

  isLoggedIn(): boolean {
    return this.getAuth() !== null;
  }

  hasRole(role: Roles) {
    const auth = this.getAuth();
    if (!auth) {
      return false;
    }

    return auth.roles.some((r) => r === role);
  }

  hasRoles(roles: Roles[]) {
    const auth = this.getAuth();
    if (!auth) {
      return false;
    }

    if (roles != null) {
      const result = auth.roles.some((r) => roles.some((rr) => rr === r));

      return result;
    }

    return false;
  }

  hasGroup(groupIds: number[]) {
    const auth = this.getAuth();
    if (!auth) {
      return false;
    }

    if (auth.groupRoles != null && groupIds != null) {
      const result = auth.groupRoles.some((r) =>
        groupIds.some((rr) => rr === r.groupId)
      );

      return result;
    }

    return false;
  }

  getAuth(): AuthToken {
    if (!this.authToken) {
      const auth = localStorage.getItem(this.authKey);
      if (auth) {
        this.authToken = JSON.parse(auth);
      }
    }

    if (this.authToken && this.tokenIsValid()) {
      return this.authToken;
    }

    this.logout();
    return null;
  }

  private tokenIsValid(): boolean {
    const tokenExpirationDate = new Date(this.authToken.tokenExpiration);
    return tokenExpirationDate > new Date();
  }

  reset2FAForUser(id: number) {
    return this.http.post(
      `${environment.apiUrls.security}/auth/reset-2fa/${id}`,
      null
    );
  }
}
